import { Component, Input, OnInit } from '@angular/core';
import { Observable, concat, of, throwError } from 'rxjs';
import { map, switchMap, toArray } from 'rxjs/operators';
import { BaseService } from 'src/modules/base/shared/services/base.service';

const APPLICATION = 'aiml-gateway';
const TRIGGER_RESOURCE = 'ai_agent_trigger';
/** Where events are defined: alertmanager's system (karexpert) event masters with the agent channel on. */
const EVENT_APPLICATION = 'alertmanager';
const EVENT_RESOURCE = 'event_master';
const EVENT_QUERY = 'GET_AGENT_EVENTS';

/** The gateway's allowed condition operators (TriggerConditions), with what each needs. */
export const OPERATORS: { id: string; label: string; value: 'one' | 'list' | 'number' | 'none' }[] = [
    { id: 'eq', label: 'equals', value: 'one' },
    { id: 'neq', label: 'does not equal', value: 'one' },
    { id: 'in', label: 'is one of', value: 'list' },
    { id: 'nin', label: 'is none of', value: 'list' },
    { id: 'contains', label: 'contains', value: 'one' },
    { id: 'gt', label: '>', value: 'number' },
    { id: 'gte', label: '≥', value: 'number' },
    { id: 'lt', label: '<', value: 'number' },
    { id: 'lte', label: '≤', value: 'number' },
    { id: 'exists', label: 'is present', value: 'none' },
    // Stored as {op: exists, value: false}.
    { id: 'absent', label: 'is absent', value: 'none' },
];

/** The envelope fields a message template may use besides data.* (MessageTemplate.ENVELOPE_FIELDS). */
const ENVELOPE_FIELDS = ['event_type', 'event_id', 'application', 'event_domain', 'facility_id', 'occurred_at', 'correlation_id'];

/** A variable: a data field name such as subject or customer.tier (MessageTemplate.VARIABLE). */
const VARIABLE = /^[A-Za-z0-9_]+(\.[A-Za-z0-9_]+)*$/;
const MAX_VARIABLES = 30;
const PLACEHOLDER = /\{\{\s*([A-Za-z0-9_.]+)\s*}}/;

/** data.<field>[.<field>…] or facility_id (TriggerConditions.isPath). */
const CONDITION_PATH = /^(data(\.[A-Za-z0-9_]+)+|facility_id)$/;

/** A condition value's type: events have no schema, so the rule says how to read what was typed. */
export const VALUE_TYPES: { id: ValueType; label: string }[] = [
    { id: 'text', label: 'text' },
    { id: 'number', label: 'number' },
    { id: 'boolean', label: 'yes / no' },
];
type ValueType = 'text' | 'number' | 'boolean';

/** One alertmanager event master with the agent channel on. */
interface EventDefinition {
    eventType: string;
    displayName: string;
    /** Its eventChannels.agent_application (application_master application_name). */
    application: string;
    /** Activity, or Scheduled (sent on a schedule). */
    domain: string;
}

interface ConditionRow {
    /** data.<field> of the alert's resource, or facility_id. */
    path: string;
    op: string;
    /** As typed; converted to {@link type} when saved. */
    value: string;
    type: ValueType;
}

/** One trigger as the editor holds it; `id` once stored. */
export interface TriggerRow {
    id?: string;
    name: string;
    /** The event master's eventChannels.agent_application. */
    application: string;
    /** The event master's eventId. */
    eventType: string;
    /** The data fields the payload sends the agent. */
    variables: string[];
    combinator: 'all' | 'any';
    conditions: ConditionRow[];
    messageTemplate: string;
    maxRetries: number;
    enabledOnPublish: boolean;
    state: string;
    dirty: boolean;
    /** The last TEST_TRIGGER answer for this row. */
    test?: TriggerTest | null;
    testing?: boolean;
    /** Field names of the latest event of this type, offered as variables. */
    fields?: string[];
    fieldsNote?: string;
    /** The variable being typed in. */
    newVariable?: string;
}

/** TEST_TRIGGER's answer; sample is "request" or the id of the stored event it used. */
export interface TriggerTest {
    matched: boolean;
    message: string;
    sample: string;
    /** The sample's data field names. */
    fields: string[];
}

function firstRow(data: any): any {
    return Array.isArray(data?.resource) ? data.resource[0] : data?.resource;
}

function typeOf(value: any): ValueType {
    return typeof value === 'number' ? 'number' : typeof value === 'boolean' ? 'boolean' : 'text';
}

/**
 * The guided wizard's Triggers step: event triggers of one guided master (ai_agent_trigger,
 * source_setup_id). An event is an alertmanager system event master with agent on, chosen by
 * application then event; an account's facility gets it through Alert Copy. When it happens the
 * agent receives the trigger payload: the event's details, the variables picked here and the
 * instructions. Edits stay here until the wizard saves the setup and calls {@link sync}; the
 * gateway checks every trigger again on save (variables, conditions, instructions). State is the gateway's: draft until the agent is published, then
 * enabled or disabled.
 */
@Component({
    selector: 'app-agent-trigger-editor',
    templateUrl: './agent-trigger-editor.component.html',
    styleUrls: ['./ai-guided-setup-wizard.component.scss', './agent-trigger-editor.component.scss'],
    standalone: false
})
export class AgentTriggerEditorComponent implements OnInit {

    /** The guided master; null until its first save. */
    @Input() setupId: string | null = null;
    /** An account's copy: triggers belong to the master, so they are shown, not edited. */
    @Input() readOnly = false;

    readonly operators = OPERATORS;
    readonly valueTypes = VALUE_TYPES;
    readonly envelopeFields = ENVELOPE_FIELDS;

    definitions: EventDefinition[] = [];
    triggers: TriggerRow[] = [];
    loading = false;
    loadError = '';
    /** Stored triggers removed in the editor, deleted on the next sync. */
    private removedIds: string[] = [];

    constructor(private readonly baseService: BaseService) { }

    ngOnInit(): void {
        this.loading = true;
        this.baseService.getData({}, '', EVENT_RESOURCE, EVENT_APPLICATION, EVENT_QUERY).subscribe({
            next: (data: any) => {
                if (data?.errCode !== 0) {
                    this.loading = false;
                    this.loadError = data?.message || 'Could not load the alert events.';
                    return;
                }
                this.definitions = (Array.isArray(data.resource) ? data.resource : []).map((r: any) => ({
                    eventType: r.eventId,
                    displayName: r.eventDesc || r.eventId,
                    application: r.eventChannels?.agent_application || '',
                    domain: r.eventDomain || '',
                }));
                this.loadTriggers();
            },
            error: () => { this.loading = false; this.loadError = 'Could not load the alert events.'; },
        });
    }

    private loadTriggers(): void {
        if (!this.setupId) {
            this.loading = false;
            return;
        }
        this.baseService.getData({ args: 'source_setup_id:' + this.setupId }, '', TRIGGER_RESOURCE, APPLICATION, 'GET_ALL').subscribe({
            next: (data: any) => {
                this.loading = false;
                if (data?.errCode !== 0) {
                    this.loadError = data?.message || 'Could not load the triggers.';
                    return;
                }
                this.triggers = (Array.isArray(data.resource) ? data.resource : []).map((r: any) => this.fromStored(r));
                if (!this.readOnly) {
                    this.triggers.forEach(row => this.loadFields(row));
                }
            },
            error: () => { this.loading = false; this.loadError = 'Could not load the triggers.'; },
        });
    }

    // ------------------------------------------------------------- lists

    get applications(): string[] {
        return Array.from(new Set(this.definitions.map(d => d.application))).sort();
    }

    eventsOf(application: string): EventDefinition[] {
        return this.definitions.filter(d => d.application === application);
    }

    definitionOf(row: TriggerRow): EventDefinition | undefined {
        return this.definitions.find(d => d.eventType === row.eventType);
    }

    /** Latest-event fields not yet variables. */
    suggestionsOf(row: TriggerRow): string[] {
        return (row.fields || []).filter(f => !row.variables.includes(f));
    }

    operatorOf(id: string) {
        return OPERATORS.find(o => o.id === id);
    }

    get count(): number {
        return this.triggers.length;
    }

    // ------------------------------------------------------------- edits

    addTrigger(): void {
        const first = this.definitions[0];
        const row: TriggerRow = {
            name: '',
            application: first ? first.application : '',
            eventType: first ? first.eventType : '',
            variables: [],
            combinator: 'all',
            conditions: [],
            messageTemplate: first ? this.defaultTemplate(first) : '',
            maxRetries: 2,
            enabledOnPublish: true,
            state: 'draft',
            dirty: true,
        };
        this.triggers.push(row);
        this.loadFields(row);
    }

    removeTrigger(index: number): void {
        const [row] = this.triggers.splice(index, 1);
        if (row?.id) {
            this.removedIds.push(row.id);
        }
    }

    onApplicationChange(row: TriggerRow): void {
        const first = this.eventsOf(row.application)[0];
        row.eventType = first ? first.eventType : '';
        this.onEventChange(row);
    }

    onEventChange(row: TriggerRow): void {
        row.conditions = [];
        row.variables = [];
        this.loadFields(row);
        const definition = this.definitionOf(row);
        if (definition && !row.messageTemplate.trim()) {
            row.messageTemplate = this.defaultTemplate(definition);
        }
        this.touch(row);
    }

    addCondition(row: TriggerRow): void {
        row.conditions.push({ path: 'data.', op: 'eq', value: '', type: 'text' });
        this.touch(row);
    }

    removeCondition(row: TriggerRow, index: number): void {
        row.conditions.splice(index, 1);
        this.touch(row);
    }

    addVariable(row: TriggerRow, name?: string): void {
        const variable = (name ?? row.newVariable ?? '').trim().replace(/^data\./, '');
        if (!variable || row.variables.includes(variable)) {
            row.newVariable = '';
            return;
        }
        row.variables.push(variable);
        row.newVariable = '';
        this.touch(row);
    }

    removeVariable(row: TriggerRow, variable: string): void {
        row.variables = row.variables.filter(v => v !== variable);
        this.touch(row);
    }

    /** Field names of the latest event of the row's type (TEST_TRIGGER), to pick variables from. */
    private loadFields(row: TriggerRow): void {
        row.fields = [];
        row.fieldsNote = '';
        if (!row.eventType) {
            return;
        }
        const payload = this.toPayload(row, this.setupId || '');
        delete payload.id;
        this.expectOk(this.baseService.action(payload, 'TEST_TRIGGER', APPLICATION, TRIGGER_RESOURCE),
            'No event of this type has arrived yet.').subscribe({
                next: data => { row.fields = firstRow(data)?.extra_data?.fields || []; },
                error: (error: unknown) => {
                    // The gateway's "no event" answer is worded for API callers; anything else is shown as it is.
                    const noEvent = typeof error !== 'string' || /has arrived yet/.test(error);
                    const reason = noEvent ? 'No event of this type has arrived yet' : error.replace(/[.\s]*$/, '');
                    row.fieldsNote = `${reason}. Type the field names instead.`;
                },
            });
    }

    /** Puts {{placeholder}} at the end of the template. */
    insertField(row: TriggerRow, path: string): void {
        const spacer = row.messageTemplate && !/\s$/.test(row.messageTemplate) ? ' ' : '';
        row.messageTemplate = `${row.messageTemplate}${spacer}{{${path}}}`;
        this.touch(row);
    }

    touch(row: TriggerRow): void {
        row.dirty = true;
        row.test = null;
    }

    /** Names only envelope fields: which data fields the agent gets is the author's choice of variables. */
    private defaultTemplate(definition: EventDefinition): string {
        return `A ${definition.displayName} event ({{event_id}}) happened in {{application}}, facility {{facility_id}}. `
            + 'Use the trigger payload to decide what to do.';
    }

    // ------------------------------------------------------------- gateway

    /** The trigger as ai_agent_trigger stores it. */
    private toPayload(row: TriggerRow, setupId: string): any {
        const rules = row.conditions
            .filter(c => c.path && c.op)
            .map(c => this.toRule(c));
        return {
            ...(row.id ? { id: row.id } : {}),
            name: row.name.trim(),
            trigger_type: 'app_event',
            source_setup_id: setupId,
            application: row.application,
            event_type: row.eventType,
            variables: row.variables,
            conditions: rules.length ? { [row.combinator]: rules } : {},
            message_template: row.messageTemplate,
            max_retries: Number(row.maxRetries),
            enabled_on_publish: row.enabledOnPublish,
            ...(row.id && row.state !== 'draft' ? { state: row.state } : {}),
        };
    }

    /** A condition row with its value converted to the type the row names. */
    private toRule(c: ConditionRow): any {
        const operator = this.operatorOf(c.op);
        const convert = (text: string): any => {
            const t = text.trim();
            if (c.type === 'number' && t !== '' && !isNaN(Number(t))) {
                return Number(t);
            }
            if (c.type === 'boolean') {
                return /^(true|yes)$/i.test(t);
            }
            return t;
        };
        if (c.op === 'absent') {
            return { path: c.path.trim(), op: 'exists', value: false };
        }
        if (operator?.value === 'none') {
            return { path: c.path.trim(), op: c.op };
        }
        if (operator?.value === 'list') {
            return { path: c.path.trim(), op: c.op, value: c.value.split(',').map(v => convert(v)).filter(v => v !== '') };
        }
        if (operator?.value === 'number') {
            return { path: c.path.trim(), op: c.op, value: Number(c.value) };
        }
        return { path: c.path.trim(), op: c.op, value: convert(c.value) };
    }

    private fromStored(r: any): TriggerRow {
        const conditions = r.conditions || {};
        const combinator: 'all' | 'any' = conditions.any ? 'any' : 'all';
        const rules: any[] = conditions[combinator] || [];
        return {
            id: r.id,
            name: r.name || '',
            application: r.application || this.definitions.find(d => d.eventType === r.event_type)?.application || '',
            eventType: r.event_type || '',
            variables: Array.isArray(r.variables) ? r.variables : [],
            combinator,
            conditions: rules.map(rule => ({
                path: rule.path,
                op: rule.op === 'exists' && rule.value === false ? 'absent' : rule.op,
                value: Array.isArray(rule.value) ? rule.value.join(', ') : (rule.value ?? '') + '',
                type: typeOf(Array.isArray(rule.value) ? rule.value[0] : rule.value),
            })),
            messageTemplate: r.message_template || '',
            maxRetries: r.max_retries ?? 2,
            enabledOnPublish: r.enabled_on_publish !== false,
            state: r.state || 'draft',
            dirty: false,
        };
    }

    /** What is wrong with the triggers before anything is sent, or ''. */
    validate(): string {
        for (const [i, row] of this.triggers.entries()) {
            const problem = this.validateRow(row, i);
            if (problem) {
                return problem;
            }
        }
        return '';
    }

    /** What is wrong with one trigger, or ''. */
    private validateRow(row: TriggerRow, i: number): string {
        const label = row.name.trim() || `Trigger ${i + 1}`;
        if (!row.application || !row.eventType) {
            return `${label}: choose the application and event.`;
        }
        if (row.variables.length > MAX_VARIABLES) {
            return `${label}: at most ${MAX_VARIABLES} variables.`;
        }
        const badVariable = row.variables.find(v => !VARIABLE.test(v));
        if (badVariable) {
            return `${label}: variable "${badVariable}" must be a field name such as subject or customer.tier.`;
        }
        if (!row.messageTemplate.trim()) {
            return `${label}: the instructions are required.`;
        }
        const placeholder = new RegExp(PLACEHOLDER.source, 'g');
        for (let m = placeholder.exec(row.messageTemplate); m; m = placeholder.exec(row.messageTemplate)) {
            const path = m[1];
            const known = ENVELOPE_FIELDS.includes(path)
                || (path.startsWith('data.') && row.variables.includes(path.slice(5)));
            if (!known) {
                return `${label}: {{${path}}} is not a variable of this trigger; add it to the variables first.`;
            }
        }
        for (const c of row.conditions) {
            const operator = this.operatorOf(c.op);
            if (!CONDITION_PATH.test(c.path.trim())) {
                return `${label}: a condition's field must be data.<field> (e.g. data.priority) or facility_id.`;
            }
            if (c.type === 'number' && operator?.value !== 'none'
                && c.value.split(',').some(v => v.trim() !== '' && isNaN(Number(v.trim())))) {
                return `${label}: ${c.path} needs numbers.`;
            }
            if (operator?.value === 'number' && (c.value.trim() === '' || isNaN(Number(c.value)))) {
                return `${label}: ${c.path} ${operator.label} needs a number.`;
            }
            if ((operator?.value === 'one' || operator?.value === 'list') && !c.value.trim()) {
                return `${label}: ${c.path} ${operator.label} needs a value.`;
            }
        }
        return '';
    }

    /**
     * Stores the editor's changes for the setup: new triggers added, changed ones modified,
     * removed ones deleted, one request at a time. Fails with the gateway's message.
     */
    sync(setupId: string): Observable<void> {
        if (this.readOnly) {
            return of(undefined);
        }
        const problem = this.validate();
        if (problem) {
            return throwError(() => problem);
        }
        const requests: Observable<void>[] = [];
        for (const id of this.removedIds) {
            requests.push(this.expectOk(this.baseService.action({ id }, 'DELETE', APPLICATION, TRIGGER_RESOURCE),
                'Could not delete a trigger.').pipe(map(() => {
                    this.removedIds = this.removedIds.filter(r => r !== id);
                })));
        }
        for (const row of this.triggers.filter(t => t.dirty)) {
            const payload = this.toPayload(row, setupId);
            const request = row.id
                ? this.baseService.action(payload, 'MODIFY', APPLICATION, TRIGGER_RESOURCE)
                : this.baseService.add(payload, TRIGGER_RESOURCE, true, APPLICATION);
            requests.push(this.expectOk(request, `Could not save ${row.name.trim() || 'a trigger'}.`).pipe(map(data => {
                const stored = firstRow(data);
                if (stored?.id) {
                    row.id = stored.id;
                }
                if (stored?.state) {
                    row.state = stored.state;
                }
                row.dirty = false;
            })));
        }
        return requests.length ? concat(...requests).pipe(toArray(), map(() => undefined)) : of(undefined);
    }

    /** TEST_TRIGGER for one row with its current (unsaved) values and the latest event of its type. */
    testTrigger(row: TriggerRow): Observable<TriggerTest> {
        const problem = this.validateRow(row, Math.max(0, this.triggers.indexOf(row)));
        if (problem) {
            return throwError(() => problem);
        }
        const payload = this.toPayload(row, this.setupId || '');
        delete payload.id;
        return this.expectOk(this.baseService.action(payload, 'TEST_TRIGGER', APPLICATION, TRIGGER_RESOURCE),
            'Could not test the trigger.').pipe(map(data => {
                const extra = firstRow(data)?.extra_data || {};
                return {
                    matched: !!extra.matched,
                    message: extra.message || '',
                    sample: extra.sample || '',
                    fields: Array.isArray(extra.fields) ? extra.fields : [],
                };
            }));
    }

    runTest(row: TriggerRow): void {
        row.testing = true;
        row.test = null;
        this.testTrigger(row).subscribe({
            next: result => { row.testing = false; row.test = result; },
            error: (error: unknown) => {
                row.testing = false;
                row.test = { matched: false, message: typeof error === 'string' ? error : 'Could not test the trigger.', sample: '', fields: [] };
            },
        });
    }

    private expectOk(request: Observable<any>, failed: string): Observable<any> {
        return request.pipe(switchMap((data: any) => data?.errCode === 0
            ? of(data)
            : throwError(() => data?.message || failed)));
    }
}
